import { useEffect, useRef } from "react";
import { useAuth } from "@/store/auth";
import { useEvents } from "@/store/events";
import type { WSEvent } from "@/lib/types";

/**
 * Single global WS connection bound to /events. Reconnects with backoff.
 * Token is rotated automatically — when it changes, we re-connect.
 */
export function useEventsSocket(): void {
  const token = useAuth((s) => s.token);
  const base = useAuth((s) => s.base);
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!token) {
      useEvents.getState().setConnected(false);
      return;
    }

    let cancelled = false;

    function connect() {
      if (cancelled) return;
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = base ? base.replace(/^https?:/, proto) : `${proto}//${window.location.host}`;
      const url = `${host}/events?token=${encodeURIComponent(token!)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        useEvents.getState().setConnected(true);
      };
      ws.onclose = () => {
        useEvents.getState().setConnected(false);
        if (cancelled) return;
        const delay = Math.min(15000, 500 * 2 ** retryRef.current++);
        timerRef.current = window.setTimeout(connect, delay);
      };
      ws.onerror = () => {
        ws.close();
      };
      ws.onmessage = (m) => {
        try {
          const evt = JSON.parse(m.data) as WSEvent;
          useEvents.getState().addEvent(evt);
        } catch {
          /* ignore */
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [token, base]);
}
